import React from 'react'
import AiHero from '../../Components/Automation/AiHero'
import WhyAIAutomation from '../../Components/Automation/WhyAIAutomation'
import TechServicesSection from '../../Components/Automation/TechServicesSection'
import IndustriesWeServe from '../../Components/Automation/IndustriesWeServe'
import WhyPartnerWithUs from '../../Components/Automation/WhyPartnerWithUs'
import { ArrowRight, CheckCircle } from 'lucide-react'
import Banner from '../../Components/Banner'
import { Helmet } from "react-helmet-async";
import WhyAIPerformance from '../../Components/Automation/WhyAIPerformance'
import BusinessBenefitsSection from '../../Components/Automation/BusinessBenefitsSection'
import WhyMobikodeSection from '../../Components/Automation/WhyMobikodeSection'
import ContactSection from '../../Components/home/ContactSection'

const featuresData = [
    {
        id: 1,
        title: "Performance Testing Services",
        description: "We test how your apps perform under real pressure covering load, stress, and more to keep your mobile, web, and APIs running smoothly.",
        image: "/assets/product/item1.png",
    },
    {
        id: 2,
        title: "Application Performance Monitoring (APM)",
        description: "We keep a close eye on your applications with smart monitoring tools, quickly spotting issues, understanding the root cause, and continuously improving performance to keep everything running smoothly.",
        image: "/assets/product/item2.png",
    },
    {
        id: 3,
        title: "Business Intelligence & Reporting",
        description: "Empower everyone with accessible insights through interactive dashboards, KPI frameworks, and self-service BI. We deliver executive reporting and advanced analytics using tools like Power BI, Tableau, Looker, and Qlik.",
        image: "/assets/product/item3.png",
    },
    {
        id: 4,
        title: "Cloud & Infrastructure Performance",
        description: "We help ensure smooth cloud migration, optimize hybrid and multi-cloud performance, and test infrastructure for scalability, reliability, and real-world resilience.",
        image: "/assets/product/item4.png",
    },
    {
        id: 5,
        title: "AI-Driven Performance Optimization",
        description: "We use predictive analytics to spot bottlenecks early, build self-healing systems that fix issues automatically, and optimize resources intelligently to reduce costs and keep operations running smoothly.",
        image: "/assets/product/item5.png",
    },
];


const outcomesData = [
    {
        id: 1,
        text: "Proven expertise across Infor M3, enterprise systems, and custom platforms",
    },
    {
        id: 2,
        text: "Deep industry experience in Oil & Gas, HRTech, Coworking, and more",
    },
    {
        id: 3,
        text: "Blend of Agentic AI, automation, and cloud-native practices",
    },
    {
        id: 4,
        text: "Flexible engagement models with a focus on business outcomes",
    },
];


const approachData = [
    {
        id: 1,
        step: "01",
        title: "Seamless User Experience",
        desc: "Faster apps, happier customers always",
    },
    {
        id: 2,
        step: "02",
        title: "Resilience at Scale",
        desc: "Monitor early, prevent problems quickly",
    },
    {
        id: 3,
        step: "03",
        title: "Reduced Downtime",
        desc: "Proactive monitoring prevents issues early.",
    },
    {
        id: 4,
        step: "04",
        title: "Optimized Costs",
        desc: "We help you use cloud and infrastructure resources more wisely and efficiently.",
    },
    {
        id: 5,
        step: "05",
        title: "Future-Ready Systems",
        desc: "AI engineering that evolves naturally",
    },
];

function PerformanceEngineering() {
    return (
        <div>
            <Helmet>
                <title>
                    Performance Engineering Services | Application Performance | Mobikode
                </title>

                <meta
                    name="description"
                    content="Improve application speed, reliability, scalability, and efficiency with Mobikode's Performance Engineering Services. Get performance testing, APM, cloud optimization, BI reporting, and AI-driven performance solutions."
                />

                <meta
                    name="keywords"
                    content="Performance Engineering Services, Performance Engineering, Application Performance Engineering, Performance Testing Services, Application Performance Monitoring, APM Services, Application Performance Optimization, Cloud Performance Engineering, Infrastructure Performance, Performance Optimization Services, AI Performance Optimization, Business Intelligence Reporting, Software Performance Testing, Application Performance Monitoring Services"
                />

                <link
                    rel="canonical"
                    href="https://www.mobikode.com/performance-engineering"
                />

                <meta
                    property="og:title"
                    content="Performance Engineering Services | Application Performance | Mobikode"
                />

                <meta
                    property="og:description"
                    content="Keep applications fast, reliable, and scalable with Mobikode's performance engineering expertise across testing, monitoring, cloud infrastructure, analytics, and AI-driven optimization."
                />

                <meta
                    property="og:url"
                    content="https://www.mobikode.com/performance-engineering"
                />

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    property="og:image"
                    content="https://www.mobikode.com/assets/product/bg1.png"
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content="Performance Engineering Services | Application Performance | Mobikode"
                />

                <meta
                    name="twitter:description"
                    content="Optimize application performance with Mobikode's performance testing, APM, cloud infrastructure optimization, analytics, and AI-powered solutions."
                />

                <meta
                    name="twitter:image"
                    content="https://www.mobikode.com/assets/product/bg1.png"
                />
            </Helmet>
            <AiHero title="Performance Engineering" text="" imgData="/assets/product/bg1.png" />
            <section className="bg-white overflow-hidden py-[60px] lg:py-[90px]">
                <div className="max-w-[1440px] w-full mx-auto px-[20px] lg:px-[80px]">

                    {/* Outer Bordered Card */}
                    <div className="w-full bg-white border border-[#D5DDE8] flex flex-col lg:flex-row items-stretch">

                        {/* LEFT IMAGE (Flush to edges of the card) */}
                        <div className="w-full lg:w-full max-w-[498px] overflow-hidden bg-gray-50">
                            <img
                                src="/assets/product/img1.png"
                                alt="ai automation"
                                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                            />
                        </div>

                        {/* RIGHT CONTENT (Padded inside the card) */}
                        <div className="w-full lg:w-1/2 p-6 sm:p-10 lg:p-14 flex flex-col justify-center text-left">

                            {/* Top Orange Sub-badge */}
                            <p className="font-graphik text-[14px] sm:text-[16px] font-[500] text-[#ED6800] tracking-wide mb-3">
                                Start with the right foundation
                            </p>

                            {/* Title */}
                            <h2 className="font-sora text-[20px] lg:text-[30px] font-[600] text-[#01386A] leading-[1.25] tracking-tight mb-5">
                                Why Performance Engineering Matters
                            </h2>

                            {/* Description */}
                            <p className="font-graphik text-[14px] lg:text-[16px] text-[#49577A] leading-[1.7] font-[500]">
                                Slow applications can frustrate users, impact your revenue, and hurt your brand’s trust. At Mobikode, we take a proactive approach to performance fixing issues early, not after they happen. With smart monitoring, automation, and AI insights, we help keep your applications fast, reliable, and ready for whatever comes next.
                            </p>

                        </div>

                    </div>

                </div>
            </section>
            <WhyAIPerformance title="Our Performance Engineering Offerings" fData={featuresData} />
            {/* <TechServicesSection title="Explore Our Services" sData={services} /> */}
            {/* <IndustriesWeServe /> */}
            <BusinessBenefitsSection approachData={approachData} />
            {/* <section className="w-full bg-[#FDFDFD] pb-16 lg:pb-24 relative overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">
                    <div className="mb-[30px] lg:mb-[60px]">
                        <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left">
                            Business Benefits
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-[10px] lg:gap-[15px]">
                        {approachData.map((item) => (
                            <div
                                key={item.id}
                                className="relative lg:p-[30px] p-[16px] flex min-h-[250px] flex-col justify-between overflow-hidden group transition-all duration-300 border border-[#01386A45] hover:shadow-lg hover:-translate-y-1"
                            >
                                <div className="flex flex-col h-full w-full relative z-20">

                                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-4 leading-tight">
                                        {item.title}
                                    </h3>
                                    <div className="mt-auto relative grid grid-cols-1 grid-rows-1 items-center overflow-hidden">
                                        <p className="col-start-1 row-start-1 text-[#49577A] lg:text-[16px] text-[14px] font-graphik font-[500] leading-snug line-clamp-2 transition-all duration-500 ease-in-out translate-x-[150%] opacity-0 group-hover:translate-x-0 group-hover:opacity-100">
                                            {item.desc}
                                        </p>
                                    </div>
                                </div>
                                <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A] z-10" />
                                <div className="absolute bottom-0 left-0 h-[4px] bg-[#ED6800] w-0 group-hover:w-full transition-all duration-500 ease-out z-10" />
                            </div>
                        ))}
                    </div>

                </div>
            </section> */}
            <WhyMobikodeSection outcomesData={outcomesData} />
            {/* <section className="w-full bg-white relative overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">
                    <div>
                        <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] mb-[40px] lg:mb-[60px]">
                            Why Mobikode?
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {outcomesData.map((item) => (
                                <div
                                    key={item.id}
                                    className="relative bg-[#F0F0F0] p-8 min-h-[180px] flex items-center justify-center text-center overflow-hidden group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 rounded-sm border-t-4 border-transparent hover:border-[#F06115]"
                                >
                                    <h3 className="text-[#01386A] lg:text-[20px] text-[16px] font-[600] font-sora leading-tight relative z-10">
                                        {item.text}
                                    </h3>
                                    <div className="absolute bottom-0 right-0 w-[120px] h-[100px] pointer-events-none transition-transform duration-500 group-hover:scale-110 opacity-30">
                                        <img src="/assets/career/wrap1.svg" alt="" className="w-full h-full object-contain object-bottom-right" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section> */}
            {/* <section className="relative w-full h-[400px] lg:h-[507px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/career/img12.png"
                        alt="Agentic AI Background"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                </div>
                <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] lg:px-[80px] h-full flex items-center lg:items-center ">
                    <div className="bg-white p-8 lg:p-12 max-w-[588px] shadow-2xl rounded-sm">

                        <h2 className="text-[#01386A] text-[30px] lg:text-[40px] font-[600] font-sora leading-tight mb-6">
                            Agentic AI Services for Enterprises
                        </h2>
                        <p className="text-[#49577A] text-[16px] leading-[24px] font-graphik mb-8">
                            Helping businesses grow with smart, autonomous AI solutions that simplify work, improve efficiency, and spark everyday innovation.

                        </p>
                        <a href="/contact" className="inline-flex items-center gap-2 text-[#01386A] underline font-graphik font-semibold text-lg hover:gap-3 transition-all duration-300 group">
                            Contact Now
                            <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                        </a>

                    </div>

                </div>

            </section> */}
            <Banner title="Performance Engineering" text=" Empowering businesses with intelligent, autonomous AI solutions that drive efficiency and innovation." />
            <ContactSection/>
        </div>
    )
}

export default PerformanceEngineering
